import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Briefcase, Plus, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BusinessCard from "@/components/BusinessCard";
import SaveSearchButton from "@/components/SaveSearchButton";
import { INDUSTRIES, REVENUE_RANGES, FIJI_ISLANDS } from "@/lib/businesses";

const HERO_BG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1920&q=85";

export default function Businesses() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = {
        industry: searchParams.get("industry") || "all",
        location_island: searchParams.get("location_island") || "all",
        revenue_range: searchParams.get("revenue_range") || "all",
    };
    const setFilter = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "all") next.delete(key);
        else next.set(key, value);
        setSearchParams(next);
    };

    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.industry !== "all") params.set("industry", filters.industry);
        if (filters.location_island !== "all") params.set("location_island", filters.location_island);
        if (filters.revenue_range !== "all") params.set("revenue_range", filters.revenue_range);
        api.get(`/businesses?${params.toString()}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    }, [filters.industry, filters.location_island, filters.revenue_range]);

    const canCreate = user?.services?.includes("business_seller") || user?.roles?.includes("admin");

    return (
        <div data-testid="businesses-page">
            <section className="relative h-[44vh] flex items-end overflow-hidden">
                <img src={HERO_BG} alt="Fiji business" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep via-ocean-deep/70 to-transparent" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-2">Businesses for Sale</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold text-white max-w-3xl leading-tight">
                        Buy a Fijian business — vetted listings, NDA-protected financials.
                    </h1>
                    <p className="text-white/85 font-app text-lg mt-3 max-w-2xl">
                        Browse cafes, resorts, agribusinesses & more across Fiji. Sign an NDA to unlock revenue, EBITDA, and the seller's full pitch.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 text-muted-foreground font-app">
                        <Filter className="h-4 w-4" />
                        <span className="font-display font-semibold text-foreground">{items?.length ?? "—"}</span>
                        listing{items?.length === 1 ? "" : "s"}
                    </div>
                    {canCreate && (
                        <Button asChild data-testid="new-business-btn" className="bg-ocean-green text-white">
                            <Link to="/dashboard/businesses/new"><Plus className="h-4 w-4 mr-1" /> List my business</Link>
                        </Button>
                    )}
                </div>

                <Card className="p-4 mb-6 grid sm:grid-cols-3 gap-3" data-testid="businesses-filters">
                    <div>
                        <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground">Industry</Label>
                        <Select value={filters.industry} onValueChange={(v) => setFilter("industry", v)}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All industries</SelectItem>
                                {INDUSTRIES.map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground">Island</Label>
                        <Select value={filters.location_island} onValueChange={(v) => setFilter("location_island", v)}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All islands</SelectItem>
                                {FIJI_ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider font-app text-muted-foreground">Revenue</Label>
                        <Select value={filters.revenue_range} onValueChange={(v) => setFilter("revenue_range", v)}>
                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All revenue ranges</SelectItem>
                                {REVENUE_RANGES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </Card>

                {items === null && <Card className="p-12 text-center"><p className="font-app text-muted-foreground">Loading…</p></Card>}
                {items?.length === 0 && (
                    <Card className="p-12 text-center">
                        <Briefcase className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="font-app text-muted-foreground">No businesses match your filters yet. Be the first — <Link to="/dashboard/businesses/new" className="text-coral hover:underline">list your business</Link>.</p>
                    </Card>
                )}
                {items?.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="businesses-grid">
                        {items.map((b) => <BusinessCard key={b.id} business={b} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

